import { orderLineTotal } from './orderLineAmounts';
import * as XLSX from 'xlsx-js-style';

const BASE_HEADERS = [
  'Штрихкод',
  'Артикул',
  'Назва',
  'Ціна',
  '% знижки',
  'Ціна зі знижкою',
  'Кількість',
  'Сума після всіх знижок',
  'Знижка промокоду',
];

const RECIPIENT_HEADERS = [
  'Прізвище на кого відправлено',
  'Дата коли було відправлено',
];

const BOLD = { font: { bold: true } };

const calcDiscountPercent = (price, discountedPrice) => {
  const p = Number(price) || 0;
  const d = Number(discountedPrice) || 0;
  if (p <= 0) return 0;
  return Math.round((1 - d / p) * 100);
};

const setCellStyle = (ws, r, c, style) => {
  const addr = XLSX.utils.encode_cell({ r, c });
  const cell = ws[addr];
  if (!cell) return;
  cell.s = { ...(cell.s || {}), ...style };
};

/** Змушує колонку штрихкодів бути текстом (без scientific notation). */
const forceBarcodeColumnAsText = (ws, rowCount) => {
  for (let r = 1; r <= rowCount; r += 1) {
    const addr = XLSX.utils.encode_cell({ r, c: 0 });
    const cell = ws[addr];
    if (!cell) continue;
    cell.t = 's';
    cell.v = cell.v == null ? '' : String(cell.v);
    cell.z = '@';
  }
};

const formatShipmentDate = (value) => {
  if (value == null || value === '') return '';
  if (typeof value === 'string') {
    // "2026-03-04" or ISO
    const m = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) return `${m[3]}.${m[2]}.${m[1]}`;
    return value;
  }
  if (Array.isArray(value) && value.length >= 3) {
    const [y, m, d] = value;
    const dd = String(d).padStart(2, '0');
    const mm = String(m).padStart(2, '0');
    return `${dd}.${mm}.${y}`;
  }
  return String(value);
};

const buildItemRow = (item, { includeRecipientSurname = false, includeShipmentDate = false } = {}) => {
  const price = Number(item.price) || 0;
  const discountedPrice = item.discountedPrice != null ? Number(item.discountedPrice) : price;
  const quantity = Number(item.quantity) || 0;
  const discountPercent = calcDiscountPercent(price, discountedPrice);
  const sumDiscounted = orderLineTotal(item);
  const row = [
    item.barcode != null && item.barcode !== '' ? String(item.barcode) : '',
    item.productCode != null ? String(item.productCode) : '',
    item.name || '',
    price,
    discountPercent,
    discountedPrice,
    quantity,
    sumDiscounted,
    Number(item.promoDiscountAmount) || 0,
  ];
  if (includeRecipientSurname) {
    row.push(item.recipientSurname || '');
  }
  if (includeShipmentDate) {
    row.push(item.shipmentDateFormatted || '');
  }
  return row;
};

const calcTotals = (list) => {
  const totalWithoutDiscount = list.reduce(
    (sum, item) => sum + (Number(item.price) || 0) * (Number(item.quantity) || 0),
    0
  );
  const totalWithDiscount = list.reduce((sum, item) => sum + orderLineTotal(item), 0);
  return { totalWithoutDiscount, totalWithDiscount };
};

const writeWorkbook = ({ fileName, headers, rows, list }) => {
  const { totalWithoutDiscount, totalWithDiscount } = calcTotals(list);
  const totalLabelCol = 6;
  const totalValueCol = 7;
  const blankRowIndex = rows.length + 1;
  const totalWithoutRowIndex = blankRowIndex + 1;
  const totalWithRowIndex = blankRowIndex + 2;

  const totalWithoutRow = new Array(headers.length).fill('');
  totalWithoutRow[totalLabelCol] = 'Загальна сума без знижки';
  totalWithoutRow[totalValueCol] = totalWithoutDiscount;
  const totalWithRow = new Array(headers.length).fill('');
  totalWithRow[totalLabelCol] = 'Загальна сума зі знижкою';
  totalWithRow[totalValueCol] = totalWithDiscount;

  const aoa = [
    headers,
    ...rows,
    [],
    totalWithoutRow,
    totalWithRow,
  ];

  const ws = XLSX.utils.aoa_to_sheet(aoa);
  forceBarcodeColumnAsText(ws, rows.length);

  headers.forEach((_, c) => setCellStyle(ws, 0, c, BOLD));
  setCellStyle(ws, totalWithoutRowIndex, totalLabelCol, BOLD);
  setCellStyle(ws, totalWithoutRowIndex, totalValueCol, BOLD);
  setCellStyle(ws, totalWithRowIndex, totalLabelCol, BOLD);
  setCellStyle(ws, totalWithRowIndex, totalValueCol, BOLD);

  const cols = [
    { wch: 16 },
    { wch: 16 },
    { wch: 42 },
    { wch: 10 },
    { wch: 11 },
    { wch: 16 },
    { wch: 28 },
    { wch: 18 },
    { wch: 18 },
  ];
  if (headers.length > 9) {
    cols.push({ wch: 28 });
  }
  if (headers.length > 10) {
    cols.push({ wch: 22 });
  }
  ws['!cols'] = cols;

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Товари');
  XLSX.writeFile(wb, fileName);
};

/**
 * Експорт товарів одного замовлення в Excel.
 * @param {{ orderId: number|string, items: Array }} params
 */
export const downloadOrderItemsExcel = ({ orderId, items }) => {
  const list = Array.isArray(items) ? items : [];
  const rows = list.map((item) => buildItemRow(item));
  writeWorkbook({
    fileName: `${orderId}.xlsx`,
    headers: BASE_HEADERS,
    rows,
    list,
  });
};

/**
 * Експорт товарів кількох замовлень (+ прізвище одержувача;
 * дата відправки — лише якщо хоча б одне замовлення вже відправлено).
 * @param {{ orders: Array, fileName?: string }} params
 */
export const downloadOrdersItemsExcel = ({ orders, fileName }) => {
  const orderList = Array.isArray(orders) ? orders : [];
  const includeShipmentDate = orderList.some((order) => Boolean(formatShipmentDate(order?.shipmentDate)));

  const list = orderList.flatMap((order) => {
    const surname = order?.userInfo?.surname || '';
    const shipmentDateFormatted = formatShipmentDate(order?.shipmentDate);
    return (order?.orderItems || []).map((item) => ({
      ...item,
      recipientSurname: surname,
      shipmentDateFormatted,
    }));
  });

  const columnOptions = {
    includeRecipientSurname: true,
    includeShipmentDate,
  };
  const rows = list.map((item) => buildItemRow(item, columnOptions));
  const headers = [
    ...BASE_HEADERS,
    RECIPIENT_HEADERS[0],
    ...(includeShipmentDate ? [RECIPIENT_HEADERS[1]] : []),
  ];

  const ids = orderList.map((o) => o.orderId).filter((id) => id != null);
  const resolvedName = fileName
    || (ids.length === 1
      ? `${ids[0]}.xlsx`
      : ids.length > 0 && ids.length <= 5
        ? `${ids.slice().sort((a, b) => a - b).join('-')}.xlsx`
        : 'orders.xlsx');

  writeWorkbook({
    fileName: resolvedName,
    headers,
    rows,
    list,
  });
};
